import React, { useEffect, useState } from "react";
import useUrlParams from "../customHooks/useUrlParams";
import {
  EmpMasterEdge,
  EmpMasterOrderField,
  EmpQueryType,
  GetEmpResult,
  GetEmpVars,
} from "../components/modules/asset/users/queries/types";
import { GetEmp, GetEmpAutocomplete } from "../components/modules/asset/users/queries/queries";
import { useLazyQuery } from "@apollo/client";
import { responseType } from "../utils/formTypes";
import { FETCH_MORE_DATA, ROWS_PER_PAGE } from "../utils/constants";
import { OrderDirection } from "../utils/commonQueryTypes";
import { getModifiedScrollHeight } from "../utils/UtilFunctions";
import {
  FormAutocomplete,
  LabeledAutocomplete,
  formClasses,
  labelClasses,
} from "../styles/AutocompleteStyles";
import { TextField } from "@mui/material";
import { empType } from "../utils/Enum.types";

interface Props {
  onKeyDown:
    | ((
        event: React.KeyboardEvent<HTMLDivElement> & {
          defaultMuiPrevented?: boolean;
        }
      ) => void)
    | undefined;
  onChange: (newValue: responseType) => void;
  value: number;
  label: string;
  queryType: EmpQueryType;
  empType: empType[];
  ldgrId?: number;
  kgid?: boolean;
}

const EmployeeSelect = ({
  onChange,
  onKeyDown,
  queryType,
  empType,
  value,
  label,
  ldgrId,
  kgid,
}: Props) => {
  const { customer_id } = useUrlParams();
  const [searchData, setSearchData] = useState<string | number>("");
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [categories, setCategories] = useState<EmpMasterEdge[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<responseType[]>([]);
  const [autoValue, setAutoValue] = useState<responseType | null>(null);

  const [GetEmployees, { data, loading, fetchMore }] = useLazyQuery<
    GetEmpResult,
    GetEmpVars
  >(kgid ? GetEmpAutocomplete : GetEmp);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetEmp.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = categories.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setCategories(updatedNewData);
        setCategoryOptions(
          updatedNewData.map(({ node }) => ({
            label: kgid
              ? `${node.kgid.toString()} / ${node.metalNo.toString()}`
              : node.name,
            value: node.id,
          }))
        );
      } else {
        setCategories(newData);
        setCategoryOptions(
          newData.map(({ node }) => ({
            label: kgid
              ? `${node.kgid.toString()} / ${node.metalNo.toString()}`
              : node.name,
            value: node.id,
          }))
        );
      }
      setEndCursor(data.GetEmp.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [data, loading]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id) {
          GetEmployees({
            variables: {
              after: null,
              customer_id,
              first: ROWS_PER_PAGE,
              input: {
                queryType,
                empType: empType,
                companyID: 0,
                ldgrID: ldgrId ? ldgrId : 0,
                siteID: 0,
                subsidiaryID: 0,
              },
              orderBy: {
                direction: OrderDirection.ASC,
                field: EmpMasterOrderField.NAME,
              },
              name: searchData as string,
              kgidValue: kgid ? Number(searchData) : 0,
            },
          });
        }
      } catch (err) {
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [customer_id, GetEmployees, queryType, ldgrId, empType, searchData]);

  useEffect(() => {
    if (value) {
      const foundValue = categoryOptions.find(
        ({ value: optionsValue }) => Number(optionsValue) === Number(value)
      );

      setAutoValue(foundValue ? foundValue : null);
    } else {
      setAutoValue(null);
    }
  }, [value, data, categoryOptions]);

  const handleScroll = (e: React.UIEvent<HTMLUListElement, UIEvent>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
      if (hasNextPage && !loading) {
        fetchMore({
          variables: {
            first: FETCH_MORE_DATA,
            after: endCursor,
          },
          updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prevResult;

            const newEdges = fetchMoreResult.GetEmp.edges;
            const pageInfo = fetchMoreResult.GetEmp.pageInfo;
            setEndCursor(pageInfo.endCursor);
            setHasNextPage(pageInfo.hasNextPage);

            const duplicateCheck = prevResult.GetEmp.edges.filter(
              ({ node: { id } }) =>
                newEdges.findIndex(
                  ({ node: { id: newId } }) => newId === id
                ) !== -1
            );

            if (duplicateCheck.length > 0) return prevResult;

            return {
              GetEmp: {
                edges: [...categories, ...newEdges],
                pageInfo,
                totalCount: data ? data.GetEmp.totalCount! : 0,
              },
            };
          },
        });
      }
    }
  };

  let commonAutocompleteClassName = label
    ? labelClasses.inputRoot
    : formClasses.inputRoot;
  let commonTextFieldClassName = label
    ? labelClasses.formControlRoot
    : formClasses.formControlRoot;

  const Autocomplete =
    label.length > 0 ? LabeledAutocomplete : FormAutocomplete;

  return (
    <Autocomplete
      className={commonAutocompleteClassName}
      options={categoryOptions}
      //   onKeyDown={(e) => {
      //     if (e.key === Keys.ENTER) {
      //       e.preventDefault();
      //       handleMUISelectEvent(e);
      //     }
      //   }}
      value={autoValue}
      openOnFocus
      forcePopupIcon
      slotProps={{
        listbox: {
          onScroll: handleScroll,
        },
      }}
      onChange={(e, newValue) => {
        onChange(newValue as responseType);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          label={label}
          onChange={(e) => setSearchData(e.target.value)}
          className={commonTextFieldClassName}
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
        />
      )}
    />
  );
};

export default EmployeeSelect;
